import type { ReactNode } from 'react';
import type { NavItem, NavSection } from '@/config/navigation';

/** True when `pathname` is `to` or a descendant of it. */
export function isPathActive(pathname: string, to: string): boolean {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(`${to}/`);
}

/** True when any leaf under `item` matches `pathname`. */
export function isBranchActive(pathname: string, item: NavItem): boolean {
  if (isPathActive(pathname, item.to)) return true;
  return (item.children ?? []).some((child) => isBranchActive(pathname, child));
}

/**
 * The `to` of the single most-specific nav item matching `pathname`, plus all of
 * its ancestors — same "longest match wins" rule as {@link breadcrumbForPath}.
 * Two sibling sections can route under a shared URL prefix (e.g. Academics and
 * Timetable both live under /academics/*) without both lighting up as active:
 * only the item that actually owns the deepest match, and its ancestors, qualify.
 */
export function activeNavPath(sections: NavSection[], pathname: string): Set<string> {
  let best: { chain: string[]; length: number } | null = null;

  const visit = (item: NavItem, chain: string[]) => {
    const nextChain = [...chain, item.to];
    if (isPathActive(pathname, item.to) && (!best || item.to.length > best.length)) {
      best = { chain: nextChain, length: item.to.length };
    }
    item.children?.forEach((child) => visit(child, nextChain));
  };

  sections.forEach((section) => section.items.forEach((item) => visit(item, [])));

  return new Set(best ? (best as { chain: string[] }).chain : []);
}

/** Total number of leaf (childless) pages reachable under `items`, for a group's count badge. */
export function countLeaves(items: NavItem[]): number {
  return items.reduce(
    (sum, item) => sum + (item.children?.length ? countLeaves(item.children) : 1),
    0,
  );
}

export interface BreadcrumbEntry {
  label: string;
  /** Omitted for entries that aren't a real route (e.g. the leading role context). */
  to?: string;
}

/**
 * Breadcrumb trail for the current route — role context, then the section/branch
 * labels down to the page itself, for the AppBar breadcrumb + document title.
 */
export function breadcrumbForPath(sections: NavSection[], pathname: string): BreadcrumbEntry[] {
  let best: { trail: BreadcrumbEntry[]; length: number } | null = null;

  const visit = (item: NavItem, trail: BreadcrumbEntry[]) => {
    const nextTrail = [...trail, { label: item.label, to: item.to }];
    if (isPathActive(pathname, item.to) && (!best || item.to.length > best.length)) {
      best = { trail: nextTrail, length: item.to.length };
    }
    item.children?.forEach((child) => visit(child, nextTrail));
  };

  sections.forEach((section) => {
    const seed: BreadcrumbEntry[] = section.label ? [{ label: section.label }] : [];
    section.items.forEach((item) => visit(item, seed));
  });

  if (!best) return [];
  const trail = (best as { trail: BreadcrumbEntry[] }).trail;
  // A section and its lone branch often share a name (e.g. "Staff" > "Staff") — collapse the
  // repeat into one entry, keeping whichever copy actually has a route to link to (the section-label
  // seed never does; a plain filter would keep that one and silently strand the crumb with no `to`).
  return trail.reduce<BreadcrumbEntry[]>((acc, entry) => {
    const prev = acc[acc.length - 1];
    if (prev && prev.label === entry.label) {
      acc[acc.length - 1] = entry.to ? entry : prev;
      return acc;
    }
    acc.push(entry);
    return acc;
  }, []);
}

export interface ModuleAtPath {
  label: string;
  children: NavItem[];
}

/**
 * The module (branch nav item) whose own route exactly matches `pathname`, with
 * its display label already resolved (a single-branch section's own label takes
 * precedence over the branch's internal label — the same rule the sidebar uses).
 * Used by the module "sub-page list" view reached by clicking a sidebar module.
 */
export function moduleAtPath(sections: NavSection[], pathname: string): ModuleAtPath | null {
  for (const section of sections) {
    for (const item of section.items) {
      if (item.to === pathname && item.children?.length) {
        const label = section.items.length === 1 && section.label ? section.label : item.label;
        return { label, children: item.children };
      }
    }
  }
  return null;
}

export interface ModuleCard {
  label: string;
  to: string;
  icon?: ReactNode;
  description?: string;
}

export interface ModuleGroup {
  label: string;
  cards: ModuleCard[];
}

/**
 * Section groups for a "quick access" card grid (e.g. the dashboard home) —
 * one card per top-level nav item, using the same single-branch label
 * collapsing rule as the sidebar and breadcrumbs. `excludeTo` skips items
 * that already have a dedicated spot elsewhere on the page (e.g. the
 * dashboard's own entry pointing back at itself).
 */
export function moduleGroups(sections: NavSection[], excludeTo: string[] = []): ModuleGroup[] {
  return sections
    .map((section) => ({
      label: section.label ?? 'Overview',
      cards: section.items
        .filter((item) => !excludeTo.includes(item.to))
        .map((item) => ({
          label: section.items.length === 1 && section.label ? section.label : item.label,
          to: item.to,
          icon: item.icon,
          description: item.description,
        })),
    }))
    .filter((group) => group.cards.length > 0);
}

/**
 * Every nav item (page or module, at any depth) whose label matches `query`,
 * flattened for the sidebar's inline search — a single-branch section's own
 * label is used in place of its branch's internal label, same rule as the sidebar.
 */
export function searchNavItems(sections: NavSection[], query: string): NavItem[] {
  const term = query.trim().toLowerCase();
  if (!term) return [];

  const results: NavItem[] = [];
  const seen = new Set<string>();
  const push = (item: NavItem, label: string) => {
    if (seen.has(item.to)) return;
    if (!label.toLowerCase().includes(term)) return;
    seen.add(item.to);
    results.push(label === item.label ? item : { ...item, label });
  };

  sections.forEach((section) => {
    section.items.forEach((item) => {
      const label = section.items.length === 1 && section.label ? section.label : item.label;
      push(item, label);
      item.children?.forEach((child) => push(child, child.label));
    });
  });

  return results;
}
